import type { TapKitConfig, TapKitInitParams, TapKitInstance } from "@coxwave/tap-kit-types";
import { loadCDNLoader } from "./loader";

/**
 * TapKit - Official TapKit Web SDK
 */
export class TapKit implements TapKitInstance {
  private instance: TapKitInstance | null = null;
  #loading: Promise<void>;
  #config: TapKitConfig;
  #loadError: Error | null = null;
  #eventsProxy?: TapKitInstance["events"]; // Cached proxy for events API
  #videoProxy?: TapKitInstance["video"]; // Cached proxy for video API

  constructor(config: TapKitConfig) {
    this.#config = config;
    this.#loading = this.load();
  }

  private async load(): Promise<void> {
    try {
      await loadCDNLoader();

      if (!window.TapKit) {
        throw new Error("TapKit not available after loading CDN loader");
      }

      this.instance = new window.TapKit(this.#config);
    } catch (err) {
      this.#loadError = err instanceof Error ? err : new Error(String(err));
      throw this.#loadError;
    }
  }

  /**
   * Promise that resolves when CDN is loaded and TapKit instance is available
   * (ready to call init())
   *
   * @example
   * ```typescript
   * const kit = new TapKit({ apiKey });
   * await kit.loaded; // CDN downloaded, window.TapKit available
   * await kit.init({...}); // Now safe to call init()
   * ```
   *
   * @see ready - For waiting until AFTER init() completes (iframe initialized)
   */
  get loaded(): Promise<void> {
    return this.#loading.then(() => {
      if (this.#loadError) {
        throw this.#loadError;
      }
      if (!this.instance) {
        throw new Error(
          `TapKit instance not initialized after loading. This may indicate a CDN loading failure for apiKey: ${this.#config.apiKey}`
        );
      }
    });
  }

  /**
   * Promise that resolves when SDK is fully initialized
   * (after init() is called and iframe is ready)
   *
   * @example
   * ```typescript
   * const kit = new TapKit({ apiKey });
   * await kit.init({...});
   * await kit.ready; // iframe is fully initialized
   * ```
   *
   * @see loaded - For waiting until CDN load completes (before init)
   */
  get ready(): Promise<void> {
    return this.#loading.then(() => {
      if (this.#loadError) {
        throw this.#loadError;
      }
      if (!this.instance) {
        throw new Error(
          `TapKit instance not initialized after loading. This may indicate a CDN loading failure for apiKey: ${this.#config.apiKey}`
        );
      }
      return this.instance.ready;
    });
  }

  /**
   * Create a proxy for video API that waits for ready before delegating
   */
  private createVideoProxy(): TapKitInstance["video"] {
    return {
      bind: (config, clipId) => {
        // Queue the bind call - will execute after ready
        this.ready.then(() => {
          this.instance?.video.bind(config, clipId);
        });
      },
      unbind: () => {
        // Unbind can be called immediately if instance exists
        this.instance?.video?.unbind();
      },
    };
  }

  /**
   * Create events API that returns unsubscribe functions synchronously
   * Queues the actual subscription until ready, but returns unsubscribe immediately
   */
  private createEventsProxy(): TapKitInstance["events"] {
    return {
      seekTimeline: (params) => {
        this.ready.then(() => {
          this.instance?.events.seekTimeline(params);
        });
      },
      onTimelineSeek: (callback) => {
        let unsubscribe: (() => void) | undefined;
        let cancelled = false;

        // Queue subscription
        this.ready.then(() => {
          if (cancelled) return;
          unsubscribe = this.instance?.events.onTimelineSeek(callback);
        });

        // Return synchronous unsubscribe function
        return () => {
          cancelled = true;
          unsubscribe?.();
        };
      },
      onAlarmFadeIn: (handler) => {
        let unsubscribe: (() => void) | undefined;
        let cancelled = false;

        this.ready.then(() => {
          if (cancelled) return;
          unsubscribe = this.instance?.events.onAlarmFadeIn(handler);
        });

        return () => {
          cancelled = true;
          unsubscribe?.();
        };
      },
    };
  }

  get events() {
    // Cache proxy to avoid creating new object on every access
    if (!this.#eventsProxy) {
      this.#eventsProxy = this.createEventsProxy();
    }
    return this.#eventsProxy;
  }

  get isOpen(): boolean {
    return this.instance?.isOpen ?? false;
  }

  get isInitialized(): boolean {
    return this.instance?.isInitialized ?? false;
  }

  get video() {
    // Cache proxy to avoid creating new object on every access
    if (!this.#videoProxy) {
      this.#videoProxy = this.createVideoProxy();
    }
    return this.#videoProxy;
  }

  async init(params: TapKitInitParams): Promise<() => void> {
    await this.#loading;
    if (!this.instance) {
      throw new Error("TapKit instance not available after loading");
    }
    return await this.instance.init(params);
  }

  destroy(): void {
    if (this.instance) {
      this.instance.destroy();
      this.instance = null;
    }
    // Clear proxy references to prevent memory leaks
    this.#eventsProxy = undefined;
    this.#videoProxy = undefined;
  }

  show(): void {
    if (this.instance) {
      this.instance.show();
    }
  }

  hide(): void {
    if (this.instance) {
      this.instance.hide();
    }
  }

  setCourse(course: Parameters<TapKitInstance["setCourse"]>[0]): void {
    if (this.instance) {
      this.instance.setCourse(course);
    }
  }

  setExternalReferenceId(id?: string): void {
    if (this.instance) {
      this.instance.setExternalReferenceId(id);
    }
  }

  setExternalReferenceData(data?: string): void {
    if (this.instance) {
      this.instance.setExternalReferenceData(data);
    }
  }

  sendContext(contextType: string, payload: Record<string, unknown>): void {
    if (this.instance) {
      this.instance.sendContext(contextType, payload);
    }
  }

  get [Symbol.toStringTag]() {
    return "TapKit";
  }
}
